import { FunctionComponent, ReactNode } from 'react';

import { Box } from '@mui/material';

interface IPaperFrameProps {
  children: ReactNode;
  width?: number;
  /** Drawn on a tinted ground when this sheet is a document rather than an invoice. */
  tone?: 'white' | 'muted';
}

/**
 * A sheet of A4, drawn.
 *
 * The counterpart to `PhoneFrame`, and the other half of the argument this page keeps making: a
 * mark drawn for a letterhead and a mark drawn for a phone header are rarely the same shape, and a
 * school is far more likely to believe that having seen both side by side than having been told.
 *
 * Deliberately white rather than themed. Paper is paper — an invoice does not follow the app's
 * appearance setting, and drawing it as though it might would be the same kind of lie the app
 * palette exists to avoid.
 */
export const PaperFrame: FunctionComponent<IPaperFrameProps> = (props: IPaperFrameProps) => {

  const { children, width = 208, tone = 'white' } = props;

  return (
    <Box
      aria-hidden
      sx={{
        width,
        aspectRatio: '1 / 1.414',
        backgroundColor: tone === 'white' ? '#FFFFFF' : 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 3,
        p: 2,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {children}
    </Box>
  );
};

interface ISkeletonLineProps {
  width: number | string;
  height?: number;
  colour?: string;
}

/**
 * One line of stand-in text. Every illustration in this folder writes its filler with this, so a
 * drawn invoice, a drawn email and a drawn notice all read as the same hand.
 */
export const SkeletonLine: FunctionComponent<ISkeletonLineProps> = ({ width, height = 4, colour }) => (
  <Box sx={{
    height,
    width: typeof width === 'number' ? `${width}%` : width,
    borderRadius: 1,
    backgroundColor: colour ?? 'action.hover'
  }} />
);
